import { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox, Popover, Toggle, Button, Paragraph } from '@ets/design-system';

import { COLUMNS_TRANSLATION_IDS } from '_common/consts';
import useVeevaIntegration from '_common/hooks/useVeevaIntegration';
import { useEditUserSettingsMutation, useGetUserSettingsQuery } from '_common/services/api/UserApi';

const TABLE_COLUMNS = [
  'status',
  'time.modification',
  'shared_with',
  'owner',
  'time.creation',
] as const;

const SEARCH_TABLE_COLUMNS = [
  'status',
  'time.modification',
  'location',
  'shared_with',
  'owner',
  'time.creation',
] as const;

type TableColumn =
  | (typeof TABLE_COLUMNS)[number]
  | (typeof SEARCH_TABLE_COLUMNS)[number]
  | 'metadata.veeva_doc_number'
  | 'metadata.veeva_global_id';

function useTableColumns(table: keyof ApiSchemas['UserSettingsSchema']['tables']) {
  const intl = useIntl();
  const { data } = useGetUserSettingsQuery();
  const hasVeevaIntegration = useVeevaIntegration();

  const tableColumns = useMemo<TableColumn[]>(() => {
    if (hasVeevaIntegration && table === 'explorer') {
      return [...TABLE_COLUMNS, 'metadata.veeva_doc_number', 'metadata.veeva_global_id'];
    } else if (table === 'search') {
      return [...SEARCH_TABLE_COLUMNS];
    }
    return [...TABLE_COLUMNS];
  }, [hasVeevaIntegration, table]);

  return useMemo(() => {
    if (!data) return [];

    return tableColumns.map((column) => ({
      id: column,
      label: COLUMNS_TRANSLATION_IDS[column]
        ? intl.formatMessage({ id: COLUMNS_TRANSLATION_IDS[column] })
        : '',
      checked: data.tables[table][column],
    }));
  }, [data, tableColumns]);
}

export type Column = {
  id: string;
  label: string;
  checked: boolean;
};
type TableColumnsToggleProps = { table: keyof ApiSchemas['UserSettingsSchema']['tables'] };

const TableColumnsToggle: FC<TableColumnsToggleProps> = ({ table }) => {
  const [editUserSettings] = useEditUserSettingsMutation();
  const columns = useTableColumns(table);
  const [isOpen, setIsOpen] = useState(false);

  const [options, setOptions] = useState([...columns]);

  useEffect(() => {
    setOptions([...columns]);
  }, [isOpen]);

  const onChange = (columnId: string) => {
    const index = options.findIndex((column) => column.id === columnId);
    const newColumnsState = [...options];
    newColumnsState.splice(index, 1, { ...options[index], checked: !options[index].checked });
    setOptions(newColumnsState);
  };

  const onClick = () => {
    const newSettings = options.reduce<
      ApiOperations['edit_user_settings']['requestBody']['content']['multipart/form-data']
    >((previous, current) => {
      //@ts-expect-error The metadata.veeva_doc_number and metadata.veeva_global_id may not exist
      previous[`tables.${table}.${current.id}`] = current.checked;
      return previous;
    }, {});
    editUserSettings(newSettings);
    setIsOpen(false);
  };

  const isButtonDisabled = useMemo(() => {
    return options.every((option, index) => option.checked === columns[index].checked);
  }, [options, columns]);

  return (
    <>
      <Popover.Root placement="bottom-end" open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
        <Popover.Trigger>
          <Toggle
            size="m"
            variant="link"
            testId="display-columns"
            isDropdown
            style={{ margin: '0 0 0 2rem' }}
            isToggled={isOpen}
            onClick={() => setIsOpen(!isOpen)}
          >
            <FormattedMessage id="DISPLAY_COLUMNS" />
          </Toggle>
        </Popover.Trigger>

        <Popover.Content testId="display-columns" className="w-120 p-8">
          <Paragraph size="m" weight="500">
            <FormattedMessage id="DISPLAY_COLUMNS" />
          </Paragraph>
          <div className="mt-8 flex flex-col gap-4">
            {options.map(({ id, label, checked }, index) => (
              <Checkbox
                key={id}
                size="s"
                checked={checked ?? false}
                testId={id}
                onChange={() => onChange(id)}
              >
                {label}
              </Checkbox>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <Button
              size="s"
              testId="display-columns-apply"
              variant="primary"
              onClick={onClick}
              disabled={isButtonDisabled}
              style={{ margin: '0 1rem 0 0' }}
            >
              <FormattedMessage id="global.apply" />
            </Button>
          </div>
        </Popover.Content>
      </Popover.Root>
    </>
  );
};

export default TableColumnsToggle;
